import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, take } from 'rxjs';
import { User } from '../models/user';
import { PopupDismissal } from '../models/popup-dialog';
import { PopupDialogService } from './popup-dialog.service';
import { PopupPreferencesService } from './popup-preferences.service';
import { LocalStorageService } from './local-storage.service';
import { isDataDeletionNavigation } from '../constants/data-deletion.constants';

@Injectable({ providedIn: 'root' })
export class PopupLauncherService {
  private generation = 0;
  private readonly shownUsers = new Set<string>();
  private activeDialog: MatDialogRef<unknown, PopupDismissal>;
  private activeContext: string;
  private readonly pendingStartupChecks = new Set<Promise<unknown>>();

  constructor(
    private service: PopupDialogService,
    private preferences: PopupPreferencesService,
    private storage: LocalStorageService,
    private dialogs: MatDialog,
    private router: Router
  ) {}

  private context(user: User): string {
    return JSON.stringify([user?._id, user?.currentUserRole?.role, user?.currentUserRole?.teamid]);
  }

  /** Startup checks resolve only after their own dialogs have closed. */
  async consider(user: User, startupChecks: Promise<unknown>): Promise<void> {
    const generation = ++this.generation;
    this.pendingStartupChecks.add(startupChecks);
    const finished = () => this.pendingStartupChecks.delete(startupChecks);
    startupChecks.then(finished, finished);
    const context = this.context(user);
    if (this.activeDialog && this.activeContext !== context) this.activeDialog.close();
    if (!user?._id || this.shownUsers.has(user._id)) return;
    const current = () =>
      generation === this.generation &&
      context === this.context(this.storage.getUser()) &&
      !isDataDeletionNavigation(this.router);
    try {
      await Promise.allSettled([...this.pendingStartupChecks]);
      if (!current()) return;
      if (this.dialogs.openDialogs.length) await firstValueFrom(this.dialogs.afterAllClosed.pipe(take(1)));
      if (!current()) return;
      const response = await this.service.eligible();
      if (!current() || !response.success) return;
      const definition = response.results.find(
        (dialog) => !this.preferences.isDismissed(user._id, dialog._id)
      );
      if (!definition) return;
      const component = await import('../components/dialogs/popup-dialog/popup-dialog.component');
      if (this.dialogs.openDialogs.length) await firstValueFrom(this.dialogs.afterAllClosed.pipe(take(1)));
      if (
        !current() ||
        this.shownUsers.has(user._id) ||
        this.preferences.isDismissed(user._id, definition._id)
      )
        return;
      this.activeContext = context;
      this.shownUsers.add(user._id);
      const reference = this.dialogs.open(component.PopupDialogComponent, {
        data: { dialog: definition },
        disableClose: true,
        width: 'min(1080px, 75vw)',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: '90vh',
        panelClass: 'popup-dialog-panel',
        autoFocus: 'dialog',
        ariaLabel: definition.title
      });
      this.activeDialog = reference;
      const choice = await firstValueFrom(reference.afterClosed());
      if (choice === 'remind-later' || choice === 'dont-show-again')
        this.preferences.dismiss(user._id, definition._id, choice);
      if (this.activeDialog === reference) this.activeDialog = undefined;
    } catch (_) {
      // Advertising must never block navigation or authentication.
    }
  }
}
